import RAPIER from "@dimforge/rapier3d-compat";

// Cup geometry constants — match cup.ts. The mesh group's origin is
// at the cup's base; the rigid body sits at the geometric center, so
// callers offset by CUP_CENTER_Y when syncing back to the mesh.
export const CUP_RADIUS = 0.5;
export const CUP_HEIGHT = 1.1;
export const CUP_CENTER_Y = CUP_HEIGHT / 2;

/**
 * Attach the cup's colliders (cylinder body + D-prism handle hull) to
 * an existing dynamic body. Extracted so the unfracture path can re-
 * create them after a fracture detached them — Rapier compat 0.19's
 * `Collider.setEnabled(false)` does not actually remove the collider
 * from the broadphase (verified via
 * `scripts/debug/probe-disabled-collider.mjs`), so the only reliable
 * way to make a fractured cup's body inert is to remove its colliders
 * outright and re-attach them on unfracture.
 *
 * The handle approximation is a half-cylinder (D-prism) convex hull
 * matching the torus arc from cup.ts (major radius 0.24 + tube 0.05,
 * opening toward -X from cup-local +X 0.5). The half-circle bulges +X
 * to mirror the handle's outer face; the flat chord sits against the
 * cup body.
 */
export function attachCupColliders(
  world: RAPIER.World,
  body: RAPIER.RigidBody,
): void {
  const cupColDesc = RAPIER.ColliderDesc.cylinder(CUP_HEIGHT / 2, CUP_RADIUS)
    .setMass(0.27)
    .setFriction(0.85)
    .setRestitution(0.18)
    .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS);
  world.createCollider(cupColDesc, body);

  const handleR = 0.29; // torus major (0.24) + tube (0.05)
  const handleHalfDepth = 0.05; // matches tube radius
  const handlePts: number[] = [];
  for (let i = 0; i <= 12; i++) {
    const a = -Math.PI / 2 + (i / 12) * Math.PI;
    const px = Math.cos(a) * handleR;
    const py = Math.sin(a) * handleR;
    handlePts.push(px, py, handleHalfDepth);
    handlePts.push(px, py, -handleHalfDepth);
  }
  const handleHullDesc = RAPIER.ColliderDesc.convexHull(
    new Float32Array(handlePts),
  );
  if (handleHullDesc) {
    // 0.05 = handle group-local Y (0.6) − body center Y (0.55), so the
    // collider's center lands exactly at the torus center.
    world.createCollider(
      handleHullDesc
        .setTranslation(0.5, 0.05, 0)
        .setMass(0.03)
        .setFriction(0.85)
        .setRestitution(0.18),
      body,
    );
  }
}

/** Walk by index but remove from the end so handles stay valid mid-loop. */
export function detachCupColliders(
  world: RAPIER.World,
  body: RAPIER.RigidBody,
): void {
  const handles: number[] = [];
  for (let i = 0; i < body.numColliders(); i++) {
    handles.push(body.collider(i).handle);
  }
  for (const h of handles) {
    const c = world.getCollider(h);
    if (c) world.removeCollider(c, true);
  }
}
